import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Spinner } from '@easydent/shared-ui';
import {
  beginPlatformEnrolment, completePlatformEnrolment, disablePlatformEnrolment,
  getPlatformCompany, getPlatformEnrolment, getPlatformSettings, updatePlatformCompany,
  updatePlatformSettings,
  type PlatformCompanyInput, type PlatformFiscalEnrolment,
} from '@/lib/api';

const field = 'w-full rounded-lg border border-line-strong px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none';

const EMPTY_COMPANY: PlatformCompanyInput = {
  legalName: '', tradingName: null, countryCode: 'RO', taxId: '', registrationNumber: '',
  address: '', city: '', postalCode: null, countySubdivision: '',
  iban: null, bankName: null, email: '', phone: null,
  isVatRegistered: false, defaultVatRatePercent: 0, defaultCurrency: 'RON',
  invoiceSeries: 'EDF', paymentTermsDays: 15,
};

/** Human labels for the fields an e-invoice cannot do without, keyed by the API's names. */
const REQUIRED_LABELS: Record<string, string> = {
  LegalName: 'registered company name',
  TaxId: 'tax id',
  Address: 'address',
  City: 'city',
  CountySubdivision: 'county (ISO code)',
};

/**
 * Platform settings that must be changeable without a deploy. Anything read from appsettings, env
 * or Vault needs an API and Worker restart to change — no use when the on-call address is wrong at
 * two in the morning.
 */
const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [opsEmail, setOpsEmail] = useState('');
  const [storagePrice, setStoragePrice] = useState('0');
  const [blockMb, setBlockMb] = useState(25 * 1024);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPlatformSettings()
      .then((s) => {
        setOpsEmail(s.opsEmail ?? '');
        setStoragePrice(String(s.storageAddOnPrice));
        setBlockMb(s.storageAddOnBlockMb);
      })
      .catch(() => setError(t('settings.loadError', "Could not load platform settings.")))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      const price = Number(storagePrice);
      await updatePlatformSettings({
        opsEmail: opsEmail.trim() || null,
        // Only sent when it parses. A blank or mistyped box must leave the price where it was
        // rather than reading as "make it free".
        storageAddOnPrice: Number.isFinite(price) && price >= 0 ? price : undefined,
      });
      setSaved(true);
    } catch (e: any) {
      setError(e?.response?.data?.title ?? t('common.saveError', "Could not save."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center p-10"><Spinner size="lg" /></div>;

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-1 text-2xl font-bold text-body">{t('settings.title', "Platform settings")}</h1>
      <p className="mb-6 text-sm text-muted">{t('settings.immediate', "Changes take effect immediately — no restart, no deploy.")}</p>

      {error && <Alert variant="error" className="mb-4">{error}</Alert>}
      {saved && <Alert variant="success" className="mb-4">{t('common.saved', "Saved.")}</Alert>}

      <Card>
        <h2 className="mb-1 text-sm font-semibold text-body">{t('settings.opsEmail', "Operations email")}</h2>
        <p className="mb-3 text-xs text-muted">
          Where platform notifications go: new clinics and service providers, failed payments,
          accounts about to be suspended, clinic data exports, and the daily note on which clinics
          are actually using easyDent.
        </p>
        <p className="mb-3 text-xs text-muted">
          One address or several — separate them with commas. Each person gets their own copy, so a
          bad address cannot stop the others arriving.
        </p>
        <input
          className={field}
          type="text"
          placeholder="mihesgelu@easysoft.ro, second@easysoft.ro"
          value={opsEmail}
          onChange={(e) => { setOpsEmail(e.target.value); setSaved(false); }}
        />
        {/* Said plainly, because an empty box silently disabling alerts would be a nasty surprise. */}
        <p className="mt-2 text-xs text-subtle-ink">
          Leave blank to turn platform notifications off. They are sent from the platform's own mail
          account, never a clinic's. This is separate from the SuperAdmin login — changing it here
          does not change who can sign in.
        </p>
        <div className="mt-4 border-t border-line-soft pt-3">
          <div className="mb-2 text-xs uppercase tracking-wide text-subtle-ink">{t('settings.storageAddons', "Storage add-ons")}</div>
          <label className="flex max-w-xs flex-col text-xs text-muted-strong">
            Price per {Math.round(blockMb / 1024)} GB block, per month
            <input
              className={field} type="number" min={0} step={0.5}
              value={storagePrice}
              onChange={(e) => { setStoragePrice(e.target.value); setSaved(false); }}
            />
          </label>
          {/* Zero is the shipped arrangement, not an accident — said plainly so nobody assumes
              deploying the feature started charging people. */}
          <p className="mt-2 text-xs text-subtle-ink">
            Ships at zero, which means clinics can add storage for free. Charging begins the month
            after you set a price and never reaches back over months already invoiced.
          </p>
        </div>

        <div className="mt-3">
          <Button size="sm" loading={saving} onClick={save}>{t('common.save', "Save")}</Button>
        </div>
      </Card>

      <CompanyCard />
      <EFacturaCard />
    </div>
  );
};

/**
 * The company that operates the platform — the seller on every invoice easyDent issues.
 *
 * Completeness is shown rather than assumed: five of these fields are what a compliant e-invoice
 * cannot do without, and a legal name saved without a tax id produces documents the tax authority
 * rejects. The banner names the missing field instead of saying "incomplete".
 */
const CompanyCard: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState<PlatformCompanyInput>(EMPTY_COMPANY);
  const [missing, setMissing] = useState<string[]>([]);
  const [complete, setComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPlatformCompany()
      .then((c) => {
        if (!c) return;                       // never set up — the empty form is the right state
        const { hasFiscalIdentity, missingFields, ...rest } = c;
        setForm(rest);
        setComplete(hasFiscalIdentity);
        setMissing(missingFields);
      })
      .catch(() => setError(t('settings.companyLoadError', "Could not load the company details.")))
      .finally(() => setLoading(false));
  }, []);

  const set = <K extends keyof PlatformCompanyInput>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.type === 'checkbox' ? e.target.checked
        : e.target.type === 'number' ? Number(e.target.value)
          : e.target.value;
      setForm((f) => ({ ...f, [key]: v as PlatformCompanyInput[K] }));
      setSaved(false);
    };

  const save = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      const saved = await updatePlatformCompany(form);
      setComplete(saved.hasFiscalIdentity);
      setMissing(saved.missingFields);
      setSaved(true);
    } catch (e: any) {
      setError(e?.response?.data?.title ?? e?.response?.data?.detail ?? t('common.saveError', "Could not save."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card className="mt-6"><Spinner /></Card>;

  const text = (key: keyof PlatformCompanyInput, label: string, placeholder = '') => (
    <label className="flex flex-col text-xs text-muted-strong">
      {label}
      <input
        className={field} placeholder={placeholder}
        value={(form[key] as string | null) ?? ''}
        onChange={set(key)}
      />
    </label>
  );

  return (
    <Card className="mt-6">
      <h2 className="mb-1 text-sm font-semibold text-body">{t('settings.company', "Your company")}</h2>
      <p className="mb-3 text-xs text-muted">
        The seller named on every invoice easyDent issues — to clinics, suppliers and imaging
        centres alike.
      </p>

      {error && <Alert variant="error" className="mb-3">{error}</Alert>}
      {saved && <Alert variant="success" className="mb-3">{t('common.saved', "Saved.")}</Alert>}

      {!complete && (
        <Alert variant="warning" className="mb-3">
          Not yet enough to issue an invoice — still missing{' '}
          {missing.map((m) => REQUIRED_LABELS[m] ?? m).join(', ')}.
        </Alert>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {text('legalName', 'Registered company name', 'EASYSOFTWAREDEVELOPMENT S.R.L')}
        {text('tradingName', 'Trading name', 'easyDent')}
        {text('taxId', 'Tax id (CUI)', '44609898')}
        {text('registrationNumber', 'Trade register number', 'J12/3421/2021')}
        {text('address', 'Registered address', 'Str. Magnoliei 21')}
        {text('city', 'City', 'Cluj-Napoca')}
        {text('countySubdivision', 'County (ISO 3166-2)', 'RO-CJ')}
        {text('postalCode', 'Postal code')}
        {text('countryCode', 'Country code', 'RO')}
        {text('email', 'Billing email', 'contact@easysoft.ro')}
        {text('iban', 'IBAN')}
        {text('bankName', 'Bank')}
      </div>

      {/* County is typed, never derived from the city: a wrong one passes validation and puts a
          false statement on a filed invoice. */}
      <p className="mt-2 text-xs text-subtle-ink">
        The county is the ISO 3166-2 code (RO-CJ, RO-B …). It appears on the e-invoice, so it is
        typed rather than guessed from the city.
      </p>

      <div className="mt-4 border-t border-line-soft pt-3">
        <div className="mb-2 text-xs uppercase tracking-wide text-subtle-ink">{t('settings.invoicing', "Invoicing")}</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="flex items-center gap-2 text-sm text-body-soft">
            <input type="checkbox" checked={form.isVatRegistered} onChange={set('isVatRegistered')} />
            Registered for VAT (plătitor de TVA)
          </label>
          <label className="flex flex-col text-xs text-muted-strong">
            VAT rate %
            <input
              className={field} type="number" min={0} max={100} step={0.5}
              disabled={!form.isVatRegistered}
              value={form.defaultVatRatePercent}
              onChange={set('defaultVatRatePercent')}
            />
          </label>
          {text('defaultCurrency', 'Invoice currency', 'RON')}
          {text('invoiceSeries', 'Invoice series', 'EDF')}
          <label className="flex flex-col text-xs text-muted-strong">
            Payment terms (days)
            <input
              className={field} type="number" min={0} max={365}
              value={form.paymentTermsDays} onChange={set('paymentTermsDays')}
            />
          </label>
        </div>
        <p className="mt-2 text-xs text-subtle-ink">
          If you are not registered for VAT, invoices carry none and say so. The currency here is
          used where easyDent sets the price; money collected by card is invoiced in the currency it
          was actually charged in.
        </p>
      </div>

      <div className="mt-4">
        <Button size="sm" loading={saving} onClick={save}>{t('common.save', "Save")}</Button>
      </div>
    </Card>
  );
};

/**
 * easyDent's own e-Factura enrolment.
 *
 * The certificate never reaches us: it is presented by this browser to ANAF, once, and what comes
 * back is an ordinary OAuth code. That is why enrolment is a one-time human step rather than a
 * per-invoice dependency — and why the whole of billing works without it, invoices simply waiting at
 * "Issued" until it lands.
 *
 * The callback returns to this page with ?code=…, which is why the redirect URI is this URL.
 */
const EFacturaCard: React.FC = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<PlatformFiscalEnrolment | null>(null);
  const [cif, setCif] = useState('');
  const [environment, setEnvironment] = useState<'test' | 'prod'>('test');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const redirectUri = `${window.location.origin}/settings`;

  const load = () => getPlatformEnrolment()
    .then((e) => { setState(e); setCif(e.cif ?? ''); setEnvironment(e.environment); })
    .catch(() => setError(t('settings.enrolLoadError', "Could not load the e-Factura enrolment.")))
    .finally(() => setLoading(false));

  useEffect(() => {
    // Coming back from ANAF. Finish the exchange, then clear the code out of the address bar so a
    // refresh does not try to redeem a code that has already been spent.
    const code = new URLSearchParams(window.location.search).get('code');
    if (!code) { void load(); return; }

    setBusy(true);
    completePlatformEnrolment(code, redirectUri)
      .then(() => setDone(t('settings.enrolled', 'Enrolled. easyDent can now file its own invoices.')))
      .catch((e: any) => setError(e?.response?.data?.detail ?? t('settings.anafRejected', 'ANAF did not accept the authorisation.')))
      .finally(() => {
        window.history.replaceState({}, '', '/settings');
        setBusy(false);
        void load();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const begin = async () => {
    setBusy(true); setError(null);
    try {
      window.location.href = await beginPlatformEnrolment(cif.trim(), environment, redirectUri);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.response?.data?.title ?? t('settings.enrolStartError', "Could not start the enrolment."));
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!window.confirm(t('settings.stopFiling', 'Stop filing invoices with ANAF?'))) return;
    setBusy(true); setError(null);
    try { await disablePlatformEnrolment(); await load(); }
    catch { setError(t('settings.filingOffError', "Could not turn filing off.")); }
    finally { setBusy(false); }
  };

  if (loading) return <Card className="mt-6"><Spinner /></Card>;

  return (
    <Card className="mt-6">
      <h2 className="mb-1 text-sm font-semibold text-body">{t('settings.eFacturaOwn', "e-Factura (your own invoices)")}</h2>
      <p className="mb-3 text-xs text-muted">
        Romanian e-Factura is mandatory for business-to-business invoices between residents, and
        since July 2024 VAT on an invoice that was not filed through it is not deductible — so a
        clinic that cannot deduct ours has a real grievance. This is easyDent's enrolment, separate
        from each clinic's own.
      </p>

      {error && <Alert variant="error" className="mb-3">{error}</Alert>}
      {done && <Alert variant="success" className="mb-3">{done}</Alert>}

      {!state?.platformReady && (
        <Alert variant="warning" className="mb-3">
          easyDent has no application registered with ANAF yet, so enrolment cannot start. That is
          ours to arrange — nothing here will fix it. Invoices are still numbered and stored
          meanwhile, and are filed automatically once this is sorted.
        </Alert>
      )}

      {state?.enrolled && state.canFile && (
        <Alert variant="success" className="mb-3">
          Filing as CIF {state.cif} ({state.environment === 'prod' ? 'live' : t('settings.anafTest', "ANAF test environment")}).
          {state.refreshTokenExpiresAt && (
            <> Re-authorisation needed by {new Date(state.refreshTokenExpiresAt).toLocaleDateString()}.</>
          )}
        </Alert>
      )}

      {state?.enrolled && !state.canFile && (
        <Alert variant="warning" className="mb-3">
          {state.lastError ?? t('settings.enrolledStale', 'Enrolled, but not currently able to file — re-authorise with the certificate.')}
        </Alert>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col text-xs text-muted-strong">
          Fiscal code (CUI, digits only)
          <input className={field} placeholder="44609898" value={cif} onChange={(e) => setCif(e.target.value)} />
        </label>
        <label className="flex flex-col text-xs text-muted-strong">
          Environment
          <select
            className={field}
            value={environment}
            onChange={(e) => setEnvironment(e.target.value as 'test' | 'prod')}
          >
            <option value="test">{t('settings.anafTest', "ANAF test environment")}</option>
            <option value="prod">{t('settings.live', "Live")}</option>
          </select>
        </label>
      </div>

      <p className="mt-2 text-xs text-subtle-ink">
        This must match the tax id on your company details above. The next step opens ANAF, where the
        qualified certificate is presented — it never passes through easyDent.
      </p>

      <div className="mt-4 flex gap-2">
        <Button size="sm" loading={busy} disabled={!state?.platformReady} onClick={begin}>
          {state?.enrolled ? 'Re-authorise' : t('settings.startEnrolment', 'Start enrolment')}
        </Button>
        {state?.enrolled && (
          <button
            className="rounded-lg border border-line-strong px-3 py-1.5 text-sm font-medium text-muted-strong hover:bg-canvas"
            onClick={disable}
          >
            Turn filing off
          </button>
        )}
      </div>
    </Card>
  );
};

export default SettingsPage;
